/**
 * Guards for the "External URL" document type (CWE-601).
 *
 * A document of type `url` stores an arbitrary link that the Download
 * and Preview actions later open in a new tab. Because that navigation
 * is triggered from a trusted in-app control, an attacker-supplied link
 * (phishing page, malware host) would otherwise be opened with no
 * verification. These helpers (1) reject non-http(s) schemes and
 * (2) build a confirmation dialog that names the destination host so
 * the user explicitly consents before leaving the platform.
 *
 * The backend performs the authoritative scheme check; this is the
 * matching client-side guard plus the user-facing warning.
 */

/**
 * True only for a plain http/https URL. Blocks `javascript:`, `data:`,
 * `file:`, `vbscript:` and malformed values.
 *
 * @param url - The candidate URL string.
 * @returns Whether the URL is a safe http(s) link.
 */
export function isSafeExternalUrl(url: string): boolean {
  try {
    const parsed = new URL((url || '').trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Shape consumed by the shared `useConfirm()` dialog. */
export interface ExternalNavConfirm {
  title: string;
  description: string;
  confirmLabel: string;
}

/**
 * Build the confirmation-dialog config shown before navigating to an
 * external document URL. Returns `null` when the URL is unsafe — callers
 * must treat `null` as "do not navigate".
 *
 * @param url - The external URL stored on the document.
 * @returns Dialog config naming the destination host, or `null` if unsafe.
 */
export function externalNavConfirmConfig(url: string): ExternalNavConfirm | null {
  if (!isSafeExternalUrl(url)) return null;
  const host = new URL(url.trim()).host;
  return {
    title: 'Leaving Helix',
    description:
      `This document links to an external site: ${host}. ` +
      'Helix has not verified this destination — only continue if you trust it.',
    confirmLabel: 'Continue to site',
  };
}
